import React, { useState } from 'react';
import {
  Card,
  Radio,
  Tag,
  Typography,
  List,
  Button,
  Badge,
  Space,
  Tooltip,
  Divider
} from 'antd';
import {
  CheckOutlined,
  CrownOutlined,
  StarOutlined,
  GiftOutlined,
  DollarOutlined,
  UserOutlined,
  DatabaseOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface Plan {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_cycle: 'monthly' | 'quarterly' | 'annual';
  features: string[];
  max_users: number | null;
  max_companies: number;
  storage_limit_gb: number | null;
  is_trial: boolean;
  trial_duration_days: number;
  is_active: boolean;
  sort_order: number;
}

interface PlanSelectorProps {
  plans: Plan[];
  selectedPlanId?: number;
  onSelect: (plan: Plan) => void;
  loading?: boolean;
  showFeatures?: boolean;
  layout?: 'horizontal' | 'vertical';
  highlightRecommended?: boolean;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
  plans,
  selectedPlanId,
  onSelect,
  loading = false,
  showFeatures = true,
  layout = 'horizontal',
  highlightRecommended = true
}) => {
  const [hoveredPlan, setHoveredPlan] = useState<number | null>(null);

  // Trier les plans par ordre d'affichage
  const sortedPlans = [...plans].sort((a, b) => a.sort_order - b.sort_order);

  // Déterminer le plan recommandé (généralement le plus populaire)
  const getRecommendedPlan = () => {
    // Le prix ne différencie plus les plans : l'ordre catalogue fait foi.
    return sortedPlans
      .find(p => !p.is_trial && p.is_active);
  };

  const recommendedPlan = highlightRecommended ? getRecommendedPlan() : null;

  // Obtenir l'icône du plan selon son type
  const getPlanIcon = (plan: Plan) => {
    if (plan.is_trial) return <GiftOutlined className="text-orange-500" />;
    if (recommendedPlan && plan.id === recommendedPlan.id) return <CrownOutlined className="text-gold" />;
    return <StarOutlined className="text-blue-500" />;
  };

  // Rendu d'une carte de plan
  const renderPlanCard = (plan: Plan) => {
    const isSelected = selectedPlanId === plan.id;
    const isRecommended = recommendedPlan && plan.id === recommendedPlan.id;
    const isHovered = hoveredPlan === plan.id;

    return (
      <Card
        key={plan.id}
        className={`plan-card cursor-pointer transition-all duration-200 ${
          isSelected ? 'border-primary shadow-lg' : 'border-gray-200'
        } ${isHovered ? 'shadow-md transform scale-105' : ''} ${
          !plan.is_active ? 'opacity-50' : ''
        }`}
        style={{
          borderColor: isSelected ? '#1890ff' : undefined,
          borderWidth: isSelected ? 2 : 1
        }}
        hoverable={plan.is_active}
        onClick={() => plan.is_active && onSelect(plan)}
        onMouseEnter={() => setHoveredPlan(plan.id)}
        onMouseLeave={() => setHoveredPlan(null)}
      >
        {/* En-tête du plan */}
        <div className="text-center mb-4">
          {/* Badge recommandé */}
          {isRecommended && (
            <div className="mb-2">
              <Badge.Ribbon text="Recommandé" color="gold">
                <div />
              </Badge.Ribbon>
            </div>
          )}
          
          {/* Badge d'essai */}
          {plan.is_trial && (
            <Tag color="orange" className="mb-2">
              <ClockCircleOutlined className="mr-1" />
              Essai {plan.trial_duration_days} jours
            </Tag>
          )}

          {/* Icône et nom du plan */}
          <div className="flex items-center justify-center mb-2">
            {getPlanIcon(plan)}
            <Title level={4} className="ml-2 mb-0">
              {plan.name}
            </Title>
          </div>

          {/* Description */}
          {plan.description && (
            <Text type="secondary" className="text-sm">
              {plan.description}
            </Text>
          )}
        </div>

        {/* Modèle de facturation */}
        <div className="text-center mb-4">
          <Tag color="blue">Facturation par Warehouse actif</Tag>
          <div className="text-sm text-gray-500 mt-1">
            Le plan détermine les fonctionnalités et les limites.
          </div>
        </div>

        {/* Limites et contraintes */}
        <div className="mb-4">
          <Space direction="vertical" className="w-full">
            <div className="flex items-center justify-between">
              <span className="flex items-center text-sm">
                <UserOutlined className="mr-2 text-blue-500" />
                Utilisateurs
              </span>
              <span className="font-medium">
                {plan.max_users ? plan.max_users.toLocaleString() : 'Illimité'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="flex items-center text-sm">
                <DatabaseOutlined className="mr-2 text-green-500" />
                Stockage
              </span>
              <span className="font-medium">
                {plan.storage_limit_gb ? `${plan.storage_limit_gb} GB` : 'Illimité'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="flex items-center text-sm">
                <DollarOutlined className="mr-2 text-purple-500" />
                Entreprises
              </span>
              <span className="font-medium">
                {plan.max_companies}
              </span>
            </div>
          </Space>
        </div>

        {/* Fonctionnalités */}
        {showFeatures && plan.features.length > 0 && (
          <>
            <Divider className="my-3" />
            <div>
              <Text strong className="text-sm">Fonctionnalités incluses:</Text>
              <List
                size="small"
                dataSource={plan.features.slice(0, 5)} // Limiter à 5 fonctionnalités
                renderItem={(feature) => (
                  <List.Item className="py-1 border-none">
                    <div className="flex items-center text-sm">
                      <CheckOutlined className="text-green-500 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  </List.Item>
                )}
              />
              
              {plan.features.length > 5 && (
                <Tooltip title={plan.features.slice(5).join(', ')}>
                  <Text type="secondary" className="text-xs">
                    +{plan.features.length - 5} autres fonctionnalités...
                  </Text>
                </Tooltip>
              )}
            </div>
          </>
        )}

        {/* Bouton de sélection */}
        <div className="mt-4">
          <Button
            type={isSelected ? "primary" : "default"}
            block
            disabled={!plan.is_active}
            icon={isSelected ? <CheckOutlined /> : undefined}
          >
            {isSelected ? 'Sélectionné' : plan.is_trial ? 'Essayer' : 'Choisir ce plan'}
          </Button>
        </div>

        {/* Statut du plan */}
        {!plan.is_active && (
          <div className="mt-2 text-center">
            <Tag color="red">Plan inactif</Tag>
          </div>
        )}
      </Card>
    );
  };

  // Rendu en mode radio (simple)
  const renderRadioMode = () => (
    <Radio.Group
      value={selectedPlanId}
      onChange={(e) => {
        const plan = plans.find(p => p.id === e.target.value);
        if (plan) onSelect(plan);
      }}
      className="w-full"
    >
      <Space direction="vertical" className="w-full">
        {sortedPlans.map((plan) => (
          <Radio key={plan.id} value={plan.id} disabled={!plan.is_active}>
            <div className="flex items-center justify-between w-full">
              <div>
                <span className="font-medium">{plan.name}</span>
                {plan.is_trial && (
                  <Tag color="orange" className="ml-2">
                    ESSAI
                  </Tag>
                )}
              </div>
              <div className="text-right">
                <div className="font-bold">Fonctionnalités et limites</div>
                <div className="text-sm text-gray-500">{plan.description}</div>
              </div>
            </div>
          </Radio>
        ))}
      </Space>
    </Radio.Group>
  );

  if (layout === 'vertical') {
    return (
      <div className="plan-selector">
        <Space direction="vertical" size="large" className="w-full">
          {sortedPlans.map(renderPlanCard)}
        </Space>
      </div>
    );
  }

  return (
    <div className="plan-selector">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedPlans.map(renderPlanCard)}
      </div>
    </div>
  );
};

export default PlanSelector;
